import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { UnitProvider } from "@/context/UnitContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import DashboardLayout from "@/layouts/DashboardLayout";
import Dashboard from "@/pages/Dashboard";
import Agenda from "@/pages/Agenda";
import Comandas from "@/pages/Comandas";
import Clients from "@/pages/Clients";
import Services from "@/pages/Services";
import Products from "@/pages/Products";
import Stock from "@/pages/Stock";
import Finance from "@/pages/Finance";
import Invoices from "@/pages/Invoices";
import Social from "@/pages/Social";
import Employees from "@/pages/Employees";
import Commissions from "@/pages/Commissions";
import PendingAppointments from "@/pages/PendingAppointments";
import EmployeePayments from "@/pages/EmployeePayments";
import Integrations from "@/pages/Integrations";
import Settings from "@/pages/Settings";
import Tasks from "@/pages/Tasks";
import Suppliers from "@/pages/Suppliers";
import Messages from "@/pages/Messages";
import Files from "@/pages/Files";
import GregoryAI from "@/pages/GregoryAI";
import Audit from "@/pages/Audit";
import Campaigns from "@/pages/Campaigns";
import WhatsAppJobs from "@/pages/WhatsAppJobs";
import Automations from "@/pages/Automations";
import DisparoManual from "@/pages/DisparoManual";
import Segments from "@/pages/Segments";
import WebhookDiagnostics from "@/pages/WebhookDiagnostics";
import PreLaunchCheck from "@/pages/PreLaunchCheck";
import DataSnapshots from "@/pages/DataSnapshots";
import UsersPermissions from "@/pages/UsersPermissions";
import ErrorBoundary from "@/components/ErrorBoundary";
import "@/App.css";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-foreground font-light tracking-tight">Carregando...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// 🔒 Rotas restritas ao Master (não podem ser acessadas por URL direta por operadores)
function MasterOnlyRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-foreground font-light tracking-tight">Carregando...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "master") return <Navigate to="/segmentos" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="agenda" element={<ErrorBoundary><Agenda /></ErrorBoundary>} />
        <Route path="comandas" element={<Comandas />} />
        <Route path="pendentes" element={<PendingAppointments />} />
        <Route path="clientes" element={<Clients />} />
        <Route path="servicos" element={<Services />} />
        <Route path="produtos" element={<Products />} />
        <Route path="estoque" element={<Stock />} />
        <Route path="financeiro" element={<Finance />} />
        <Route path="cobrancas" element={<Invoices />} />
        <Route path="funcionarios" element={<Employees />} />
        <Route path="comissoes" element={<Commissions />} />
        <Route path="vales-pagamentos" element={<EmployeePayments />} />
        <Route path="redes-sociais" element={<Social />} />
        <Route path="tarefas" element={<Tasks />} />
        <Route path="fornecedores" element={<Suppliers />} />
        <Route path="mensagens" element={<Messages />} />
        <Route path="arquivos" element={<Files />} />
        <Route path="gregory-ai" element={<GregoryAI />} />
        <Route path="campanhas" element={<Campaigns />} />
        <Route path="campanhas/jobs" element={<WhatsAppJobs />} />
        <Route path="automacoes" element={<MasterOnlyRoute><Automations /></MasterOnlyRoute>} />
        <Route path="segmentos" element={<Segments />} />
        <Route path="disparo-manual" element={<MasterOnlyRoute><DisparoManual /></MasterOnlyRoute>} />
        <Route path="auditoria" element={<MasterOnlyRoute><Audit /></MasterOnlyRoute>} />
        <Route path="webhook-diagnostics" element={<MasterOnlyRoute><WebhookDiagnostics /></MasterOnlyRoute>} />
        <Route path="pre-launch" element={<MasterOnlyRoute><PreLaunchCheck /></MasterOnlyRoute>} />
        <Route path="foundation/data-snapshots" element={<DataSnapshots />} />
        <Route path="usuarios" element={<MasterOnlyRoute><UsersPermissions /></MasterOnlyRoute>} />
        <Route path="integracoes" element={<Integrations />} />
        <Route path="configuracoes" element={<Settings />} />
        <Route path="google-callback" element={<Integrations />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <UnitProvider>
          <AppRouter />
          <Toaster position="top-right" />
        </UnitProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
